import * as React from 'react'

import { Card } from '@/app/features/common/Card'
import { Button } from '@/app/features/common/Button'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { Driver } from '@/app/types/creator'
import {
  FaIcon,
  faArrowDownToLineSolid,
  faArrowsRotateSolid,
  faClockDeskSolid,
  faUserQuestionSolid,
  faUsersSolid,
  type IconDefinition,
} from '@/lib/fa'
import { ImportsCardsPanel } from '@/modules/imports/pages/home/ImportsCardsPanel'
import { importsHomeCardsConfig } from '@/modules/imports/pages/home/config'
import { ImportSource } from '@/modules/imports/enums'
import { useImportsUploadingType } from '@/modules/imports/stores/importsUploadingTypeStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import { DriverCell } from '../../components/DriverCell'
import { FooterWithButtons } from '../../components/FooterWithButtons'
import {
  cardReadStatusClass,
  getCardReadStatus,
} from '../../utils/cardReadStatus'
import DividerVertical from '@/modules/drivers/shared/DividerVertical'


const DRIVER_SOURCES: ImportSource[] = [
  ImportSource.Drivers,
  ImportSource.DriverCard,
  ImportSource.File,
]

type Props = {
  companyId?: string
  drivers: Driver[]
  isLoading: boolean
  isRefreshing?: boolean
  periodStart: string
  monthLabel?: string
  onImported: () => void
  onNext: () => void
}

type StatTileProps = {
  icon: IconDefinition
  value: number
  label: string
  tone?: 'neutral' | 'warning' | 'info'
}

const StatTile = ({ icon, value, label, tone = 'neutral' }: StatTileProps) => (
  <div className="tw-flex tw-flex-1 tw-items-center tw-gap-3 tw-px-4 tw-py-3">
    <span
      className={
        tone === 'warning'
          ? 'tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-amber-50 tw-text-amber-700'
          : tone === 'info'
            ? 'tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-[#F0F3FF] tw-text-primary'
            : 'tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-base-200 tw-text-content-base'
      }
    >
      <FaIcon icon={icon} className="tw-h-4 tw-w-4" />
    </span>
    <div className="tw-flex tw-min-w-0 tw-flex-col">
      <span className="tw-text-xl tw-font-semibold tw-leading-tight">
        {value}
      </span>
      <span className="tw-text-xs tw-text-content-base/70">{label}</span>
    </div>
  </div>
)

export const ImportsStep = ({
  companyId,
  drivers,
  isLoading,
  isRefreshing = false,
  periodStart,
  monthLabel,
  onImported,
  onNext,
}: Props) => {
  const { tSafe } = useLocalize()
  const { closeUploadingType } = useImportsUploadingType()
  const [isSourcePickerOpen, setIsSourcePickerOpen] = React.useState(false)

  const handleImportSuccess = React.useCallback(() => {
    closeUploadingType()
    onImported()
  }, [closeUploadingType, onImported])

  const closeSourcePicker = React.useCallback(
    () => setIsSourcePickerOpen(false),
    [],
  )

  const unknownDrivers = React.useMemo(
    () => drivers.filter((d) => d.status === 'unknown'),
    [drivers],
  )

  const driversWithoutCurrentRead = React.useMemo(
    () =>
      drivers.filter(
        (d) =>
          d.status !== 'unknown' &&
          getCardReadStatus(d.lastCardReadDate, periodStart) !== 'ok',
      ),
    [drivers, periodStart],
  )

  const cards = React.useMemo(() => {
    const all = importsHomeCardsConfig.cards
    const driverCards = DRIVER_SOURCES.map((source) =>
      all.find((c) => c.name === source),
    ).filter((c): c is (typeof all)[number] => Boolean(c))
    const driverCardNames = new Set(driverCards.map((c) => c.name))
    return [...driverCards, ...all.filter((c) => !driverCardNames.has(c.name))]
  }, [])

  const showLoader = isLoading || isRefreshing
  const subtitle = tSafe(
    'creator.imports.subtitle',
    'Zaimportuj odczyty kart, tarczki i pozostałe dane, aby rozliczenie objęło pełny okres.',
  )

  return (
    <Card
      className="tw-flex tw-min-h-0 tw-max-w-full tw-flex-1 tw-flex-col tw-bg-base-100 tw-pb-3"
      classNameContent="!tw-flex !tw-px-0 !tw-pt-0 tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"
      paddingTop={false}
      noContentPadding
    >
      <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-pb-4 tw-pt-4">
        <div className="tw-mb-4 tw-flex tw-shrink-0 tw-flex-col tw-gap-1 tw-px-4 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
          <div>
            <h2 className="tw-mb-1 tw-text-lg tw-font-semibold">
              {tSafe(
                'creator.imports.title',
                'Wczytaj dane kierowców przed rozliczeniem',
              )}
            </h2>
            <p className="tw-text-sm tw-text-content-base/70">
              {monthLabel ? `${subtitle} (${monthLabel})` : subtitle}
            </p>
          </div>
          <div className="tw-flex tw-shrink-0 tw-gap-2">
            <Button
              size="sm"
              variant="tertiary"
              status="outlined"
              onClick={onImported}
              disabled={showLoader}
              className="tw-h-10 tw-rounded-lg tw-font-medium"
            >
              <FaIcon
                icon={faArrowsRotateSolid}
                className={showLoader ? 'tw-animate-spin' : undefined}
              />
              <span>
                {tSafe('creator.imports.refresh', 'Odśwież listę kierowców')}
              </span>
            </Button>
            <Button
              size="sm"
              disabled={!companyId}
              onClick={() => setIsSourcePickerOpen(true)}
              className="tw-h-10 tw-rounded-lg tw-font-medium"
            >
              <FaIcon icon={faArrowDownToLineSolid} />
              <span>
                {tSafe('creator.imports.importData', 'Importuj pliki')}
              </span>
            </Button>
          </div>
        </div>

        <div className="tw-mb-4 tw-flex tw-shrink-0 tw-flex-col tw-gap-3 sm:tw-flex-row">
          <StatTile
            icon={faUsersSolid}
            value={drivers.length}
            label={tSafe(
              'creator.imports.stat.total',
              'kierowców w wybranym okresie',
            )}
            tone="info"
          />
          <DividerVertical className="tw-hidden tw-self-stretch sm:tw-block" />
          <StatTile
            icon={faClockDeskSolid}
            value={driversWithoutCurrentRead.length}
            label={tSafe(
              'creator.imports.stat.missingRead',
              'bez odczytu karty obejmującego okres',
            )}
            tone={driversWithoutCurrentRead.length ? 'warning' : 'neutral'}
          />
          <DividerVertical className="tw-hidden tw-self-stretch sm:tw-block" />

          <StatTile
            icon={faUserQuestionSolid}
            value={unknownDrivers.length}
            label={tSafe(
              'creator.imports.stat.unknown',
              'nieznanych kierowców z odczytów',
            )}
            tone={unknownDrivers.length ? 'warning' : 'neutral'}
          />
        </div>

        <div className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-overflow-hidden tw-border tw-border-base-300">
          <div className="tw-shrink-0 tw-border-b tw-border-base-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium">
            {tSafe(
              'creator.imports.missingReadsTitle',
              'Kierowcy bez aktualnego odczytu karty',
            )}
          </div>
          {showLoader ? (
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center">
              <LoaderIcon className="tw-animate-spin" />
            </div>
          ) : driversWithoutCurrentRead.length ? (
            <ul className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
              {driversWithoutCurrentRead.map((driver) => {
                const status = getCardReadStatus(
                  driver.lastCardReadDate,
                  periodStart,
                )
                return (
                  <li
                    key={driver.id}
                    className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-base-300/60 tw-px-4 tw-py-2 tw-text-sm last:tw-border-b-0"
                  >
                    <DriverCell
                      name={`${driver.driver} ${driver.surname || ''}`.trim()}
                    />
                    <span className={cardReadStatusClass(status)}>
                      {driver.lastCardReadDate ??
                        tSafe('creator.imports.noRead', 'brak odczytu')}
                    </span>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-content-base/70">
              {tSafe(
                'creator.imports.missingReadsEmpty',
                'Wszyscy kierowcy mają odczyt karty obejmujący wybrany okres.',
              )}
            </div>
          )}
        </div>
      </div>

      {companyId && (
        <ImportsCardsPanel
          companyId={companyId}
          cards={cards}
          onSuccess={handleImportSuccess}
          modal={{
            open: isSourcePickerOpen,
            onClose: closeSourcePicker,
            title: tSafe('creator.imports.picker.title', 'Importuj pliki'),
            description: tSafe(
              'creator.imports.picker.description',
              'Wybierz źródło, z którego chcesz wczytać dane.',
            ),
          }}
        />
      )}

      <FooterWithButtons
        isPrev={false}
        onNext={onNext}
        className="!tw-justify-end tw-border-t tw-border-base-300"
        nextText={tSafe('creator.imports.next', 'Przejdź do wyboru kierowców')}
      />
    </Card>
  )
}
